import { api } from "@/lib/api/client"
import type { BaseResponse } from "@/types/api"
import type { DocumentLevel, DocumentType } from "@/types/domain"

export interface CreateUploadUrlRequest {
  level: DocumentLevel
  documentKey: string
  documentExtension: string
  title?: string
  description?: string
  contractId?: string
  orderId?: string
  deliveryId?: string
  batchId?: string
  lotId?: string
  logId?: string
  grade?: string
  status?: string
  statusReason?: string
}

export interface UploadUrlResponse {
  url: string
  key: string
  storedKey: string
}

export interface DownloadUrlRequest {
  type: DocumentType
  documentKey: string
  contractId?: string
  orderId?: string
  deliveryId?: string
  batchId?: string
  lotId?: string
  logId?: string
}

export interface DownloadUrlResponse {
  url: string
}

export interface DeleteDocumentRequest {
  level: DocumentType
  documentKey: string
  contractId?: string
  orderId?: string
  deliveryId?: string
  batchId?: string
  lotId?: string
  logId?: string
}

export async function createUploadUrl(
  payload: CreateUploadUrlRequest,
): Promise<UploadUrlResponse> {
  const { data } = await api.post<BaseResponse<UploadUrlResponse>>(
    "/exporter/documents",
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not get upload URL")
  }
  return data.result
}

export async function getDownloadUrl(
  params: DownloadUrlRequest,
): Promise<DownloadUrlResponse> {
  const { data } = await api.get<BaseResponse<DownloadUrlResponse>>(
    "/exporter/documents",
    { params },
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not get download URL")
  }
  return data.result
}

export async function deleteDocument(payload: DeleteDocumentRequest): Promise<void> {
  const { data } = await api.delete<BaseResponse>("/exporter/documents", {
    data: payload,
  })
  if (!data.success) {
    throw new Error(data.message ?? "Could not delete document")
  }
}

export async function uploadFileToPresignedUrl(
  url: string,
  file: File,
): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    headers: file.type ? { "Content-Type": file.type } : undefined,
  })
  if (!res.ok) {
    throw new Error(`Upload failed: ${res.status} ${res.statusText}`)
  }
}
